// Global Instructions Rule Applied!
//
// One pipeline meeting's detail: the minutes document (markdown), THIS
// meeting's follow-ups (tick done / edit / add / remove / create task), the
// per-deal follower notes (key point, summary, thesis, concerns — the next
// steps live in the follow-ups table, not as prose; each deal name opens
// that deal's timeline at its meeting note), and the transcript in a
// collapsed panel (team-visible, owner 2026-08-27).
//
// Owner 2026-09-15/16: the section is titled 'Meeting' and the meeting is
// picked from a dropdown beside it (where the date used to read) — newest first, newest by default;
// people can EDIT the minutes (a new version on the doc's supersede chain,
// stamped with them) and open a History modal listing every version with a
// line diff against the one before and a Restore action. Agent-written
// versions keep the DealBriefCard treatment; a person's edit shows a gold
// "Edited by" tag instead.
import React, { useMemo, useState } from 'react'
import { Button, Col, Collapse, Input, Modal, Popconfirm, Row, Select, Switch, Table, Tag, Tooltip } from 'antd'
import { HistoryOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { AgentMarkdown } from '../../../core/components/AgentMarkdown.tsx'
import { LineDiff } from '../../../core/components/LineDiff.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { interactionOpenTarget } from '../../internal_agents/utils/interactionOpenTarget.ts'
import { TABLE_DEALS } from '../../../state_manager/globals.ts'
import {
  MinutesVersion,
  PipelineMeetingDetail,
  PipelineMeetingFollowUp,
  PipelineMeetingListItem
} from '../../../state_manager/data_models/pipeline_meetings_model.ts'
import { MeetingDocBody } from './MeetingDocCard.tsx'
import { FollowUpTable, FollowUpStatus } from './FollowUpTable.tsx'

export const STATUS_TAG: Record<string, { color: string; label: string }> = {
  detected: { color: 'default', label: 'Detected' },
  segmenting: { color: 'blue', label: 'Analyzing' },
  notes_pending: { color: 'blue', label: 'Analyzing' },
  minutes_ready: { color: 'green', label: 'Minutes ready' },
  error: { color: 'red', label: 'Error' }
}

const MINUTES_MAX = 100000

interface MeetingMinutesDocProps {
  /** The registry, newest first — the header picker's options. */
  meetings: PipelineMeetingListItem[]
  meetingsLoading: boolean
  selectedId: string | null
  onSelectMeeting: (meetingId: string) => void
  meeting: PipelineMeetingDetail | null
  loading: boolean
  /** This meeting's follow-ups (open and done). */
  followUps: PipelineMeetingFollowUp[]
  followUpsLoading?: boolean
  onSetFollowUpStatus: (row: PipelineMeetingFollowUp, status: FollowUpStatus) => Promise<void>
  onCreateTask: (row: PipelineMeetingFollowUp) => void
  onEditFollowUp: (row: PipelineMeetingFollowUp) => void
  onAddFollowUp: () => void
  onRemoveFollowUp: (row: PipelineMeetingFollowUp) => Promise<void>
  /** Persist a person's edit of the minutes; the host refetches the detail. */
  onSaveMinutes: (content: string) => Promise<void>
  /** Make an earlier version current again; the host refetches the detail. */
  onRestoreMinutes: (docId: string) => Promise<void>
  /** Every version of this meeting's minutes, newest first (lazy, on History). */
  onLoadHistory: (meetingId: string) => Promise<MinutesVersion[]>
}

const KIND_TAG: Record<MinutesVersion['prog__kind'], { color: string; label: string }> = {
  agent: { color: 'blue', label: 'Agent-written' },
  user: { color: 'gold', label: 'Edited' },
  restore: { color: 'default', label: 'Restored' }
}

export const MeetingMinutesDoc: React.FC<MeetingMinutesDocProps> = ({
  meetings,
  meetingsLoading,
  selectedId,
  onSelectMeeting,
  meeting,
  loading,
  followUps,
  followUpsLoading = false,
  onSetFollowUpStatus,
  onCreateTask,
  onEditFollowUp,
  onAddFollowUp,
  onRemoveFollowUp,
  onSaveMinutes,
  onRestoreMinutes,
  onLoadHistory
}) => {
  const { openFormInTab } = useTabManagerContext()
  const [editOpen, setEditOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [versions, setVersions] = useState<MinutesVersion[]>([])
  const [viewId, setViewId] = useState<string | null>(null)
  const [showFull, setShowFull] = useState(false)
  const [restoring, setRestoring] = useState<string | null>(null)

  const status = meeting ? STATUS_TAG[meeting.pipeline_meetings__status] : null
  const openCount = followUps.filter((f) => f.prog__effective_status === 'open').length
  const minutes = meeting?.minutes ?? null
  const currentContent = minutes?.content ?? ''
  const versionCount = minutes?.prog__version_count ?? 0

  const pickerOptions = useMemo(
    () =>
      meetings.map((m) => ({
        value: m.pipeline_meetings__id,
        label: `${dayjs(m.pipeline_meetings__meeting_date).format('MM/DD/YYYY')} · ${m.prog__deal_count} deal${
          m.prog__deal_count === 1 ? '' : 's'
        }`,
        status: m.pipeline_meetings__status
      })),
    [meetings]
  )

  const openEdit = () => {
    setDraft(currentContent)
    setEditOpen(true)
  }

  const saveEdit = async () => {
    const content = draft.trim()
    if (!content || content === currentContent.trim()) {
      setEditOpen(false)
      return
    }
    setSaving(true)
    try {
      await onSaveMinutes(content)
      setEditOpen(false)
    } finally {
      setSaving(false)
    }
  }

  const loadHistory = async (meetingId: string, keepView = false) => {
    setHistoryLoading(true)
    try {
      const list = await onLoadHistory(meetingId)
      setVersions(list)
      setViewId((current) => (keepView && current && list.some((v) => v.pipeline_meeting_docs__id === current)
        ? current
        : list[0]?.pipeline_meeting_docs__id ?? null))
    } finally {
      setHistoryLoading(false)
    }
  }

  const openHistory = () => {
    if (!meeting) return
    setShowFull(false)
    setHistoryOpen(true)
    void loadHistory(meeting.pipeline_meetings__id)
  }

  const restore = async (version: MinutesVersion) => {
    if (!meeting) return
    setRestoring(version.pipeline_meeting_docs__id)
    try {
      await onRestoreMinutes(version.pipeline_meeting_docs__id)
      await loadHistory(meeting.pipeline_meetings__id)
    } finally {
      setRestoring(null)
    }
  }

  const viewed = versions.find((v) => v.pipeline_meeting_docs__id === viewId) ?? null
  const previous = viewed ? versions.find((v) => v.prog__revision === viewed.prog__revision - 1) ?? null : null

  const provenance = minutes?.edited_by ? (
    <Tooltip
      title={`Edited ${minutes.generated_at ? dayjs(minutes.generated_at).format('MM/DD/YYYY HH:mm') : ''} — the agent's original is in History`}
    >
      <Tag color='gold'>Edited{minutes.prog__edited_by_name ? ` by ${minutes.prog__edited_by_name}` : ''}</Tag>
    </Tooltip>
  ) : (
    <Tag color='blue'>Agent-written</Tag>
  )

  return (
    <FormSection
      data-component='MeetingMinutesDoc'
      title='Meeting'
      titleColor='green'
      loading={loading && !meeting}
      className='shadow-sm border border-gray-200'
      titleSuffix={
        <Select
          size='small'
          data-testid='meeting-picker'
          aria-label='Meeting'
          style={{ minWidth: 190 }}
          // The popup is wider than the control so the status Tag at the
          // end of each option is never clipped (asset-transactions precedent).
          popupMatchSelectWidth={300}
          value={selectedId ?? undefined}
          loading={meetingsLoading}
          options={pickerOptions}
          placeholder={meetingsLoading ? 'Loading meetings…' : 'No meetings processed yet'}
          disabled={!meetingsLoading && pickerOptions.length === 0}
          onChange={(value: string) => onSelectMeeting(value)}
          optionRender={(option) => {
            const tag = STATUS_TAG[String(option.data.status)] || { color: 'default', label: String(option.data.status) }
            return (
              <span className='flex items-center justify-between gap-2'>
                <span>{option.data.label}</span>
                <Tag color={tag.color} className='m-0'>
                  {tag.label}
                </Tag>
              </span>
            )
          }}
        />
      }
      headerActions={
        <span className='flex items-center gap-2'>
          {meeting ? provenance : null}
          {status ? <Tag color={status.color}>{status.label}</Tag> : null}
          {meeting ? (
            <>
              <Button size='small' icon={<HistoryOutlined />} disabled={!minutes} onClick={openHistory}>
                History{versionCount > 1 ? ` (${versionCount})` : ''}
              </Button>
              <Button size='small' disabled={!minutes?.content} onClick={openEdit}>
                Edit
              </Button>
            </>
          ) : null}
        </span>
      }
    >
      {!meeting && !loading ? (
        <div className='text-gray-400 text-xs py-2'>
          {meetings.length === 0 ? 'No meetings processed yet.' : 'Pick a meeting to read its minutes.'}
        </div>
      ) : null}
      {meeting ? (
        <div className='flex flex-col gap-3'>
          {minutes?.content ? (
            <MeetingDocBody doc={minutes} />
          ) : (
            <div className='text-gray-400 text-xs py-1'>
              Minutes are not ready yet — the per-deal analyses may still be in progress.
            </div>
          )}

          <div className='border-t border-gray-100 pt-2'>
            <div className='flex items-center justify-between pb-1'>
              <span className='text-[11.5px] font-semibold uppercase tracking-wide text-gray-500'>
                Follow-ups from this meeting
              </span>
              <span className='flex items-center gap-2'>
                <span className='text-[11.5px] text-gray-400'>
                  {followUps.length === 0
                    ? ''
                    : `${openCount} open · ${followUps.length - openCount} done`}
                </span>
                <Button size='small' onClick={onAddFollowUp}>
                  + Add follow-up
                </Button>
              </span>
            </div>
            <FollowUpTable
              rows={followUps}
              loading={followUpsLoading}
              onSetStatus={onSetFollowUpStatus}
              onCreateTask={onCreateTask}
              onEdit={onEditFollowUp}
              onRemove={onRemoveFollowUp}
              emptyText='No follow-ups were agreed at this meeting.'
            />
          </div>

          {meeting.deal_notes.length > 0 ? (
            <div className='flex flex-col divide-y divide-gray-100 border-t border-gray-100 pt-2'>
              <span className='text-[11.5px] font-semibold uppercase tracking-wide text-gray-500 pb-1'>
                Per-deal notes
              </span>
              {meeting.deal_notes.map((note) => {
                // The deal name opens the deal's timeline at this meeting's
                // note (the deal form itself when the note is missing).
                const target = interactionOpenTarget(TABLE_DEALS, note.deals__id, note.note_interaction_id)
                return (
                <div key={note.deals__id} className='py-2'>
                  <span className='text-[12.5px]'>
                    <button
                      type='button'
                      className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left font-semibold'
                      onClick={() =>
                        target
                          ? openFormInTab(target.module, target.id, note.deals__name || 'Deal', target.query)
                          : openFormInTab(TABLE_DEALS, note.deals__id, note.deals__name || 'Deal')
                      }
                    >
                      {note.deals__name || 'Deal'}
                    </button>
                    {note.accounts__name ? (
                      <span className='text-gray-500'> — {note.accounts__name}</span>
                    ) : null}
                  </span>
                  {note.key_point ? (
                    // The deal follower's key takeaway — the same line the
                    // deal timeline shows for this note (owner mockup 2026-09-15).
                    <div className='bg-blue-50 text-blue-900 rounded px-2 py-1 text-[13px] mt-1'>
                      {note.key_point}
                    </div>
                  ) : null}
                  {note.summary ? (
                    <div className='whitespace-pre-line text-[13px] mt-1'>{note.summary}</div>
                  ) : (
                    <div className='text-gray-400 text-xs mt-0.5'>
                      Discussed. Summary pending from the deal agent.
                    </div>
                  )}
                  {note.thesis_update ? (
                    <div className='text-[12.5px] text-gray-600 mt-1'>
                      <span className='font-semibold'>Thesis: </span>
                      {note.thesis_update}
                    </div>
                  ) : null}
                  {note.concerns ? (
                    <div className='text-[12.5px] text-gray-600 mt-0.5'>
                      <span className='font-semibold'>Concerns: </span>
                      {note.concerns}
                    </div>
                  ) : null}
                </div>
                )
              })}
            </div>
          ) : null}

          {meeting.transcript?.transcript_text ? (
            <Collapse
              size='small'
              items={[
                {
                  key: 'transcript',
                  label: 'Transcript',
                  children: (
                    <div className='whitespace-pre-line text-xs text-gray-700 max-h-96 overflow-y-auto'>
                      {meeting.transcript.transcript_text}
                    </div>
                  )
                }
              ]}
            />
          ) : null}
        </div>
      ) : null}

      {/* Edit minutes: markdown source beside a live preview (the highlight
          narrative editor precedent) — never CKEditor for an agent document. */}
      <Modal
        title='Edit minutes'
        open={editOpen}
        onOk={saveEdit}
        okText='Save'
        okButtonProps={{
          disabled: !draft.trim() || draft.trim() === currentContent.trim() || draft.length > MINUTES_MAX
        }}
        confirmLoading={saving}
        onCancel={() => setEditOpen(false)}
        destroyOnHidden
        width={1000}
      >
        <div className='text-[13px] text-gray-600 mb-2'>
          Your edit becomes the current minutes as a new version stamped with you; the agent&apos;s version
          stays in History and can be restored.
        </div>
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={12}>
            <div className='text-gray-600 mb-1 text-[12.5px]'>
              Markdown <span className='text-gray-400'>({draft.length}/{MINUTES_MAX})</span>
            </div>
            <Input.TextArea
              data-testid='minutes-editor'
              rows={22}
              value={draft}
              maxLength={MINUTES_MAX}
              onChange={(e) => setDraft(e.target.value)}
            />
          </Col>
          <Col xs={24} lg={12}>
            <div className='text-gray-600 mb-1 text-[12.5px]'>Preview</div>
            <div className='border border-gray-200 rounded p-2 bg-gray-50 overflow-y-auto' style={{ maxHeight: 520 }}>
              {minutes?.content_format === 'markdown' ? (
                <AgentMarkdown source={draft || '_(empty)_'} />
              ) : (
                <div className='whitespace-pre-line text-[13px]'>{draft}</div>
              )}
            </div>
          </Col>
        </Row>
      </Modal>

      {/* History: every version (Cap Table History precedent) with a line
          diff against the one before it, and Restore. */}
      <Modal
        title='Minutes history'
        open={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        footer={null}
        destroyOnHidden
        width={1000}
      >
        <Table
          size='small'
          rowKey='pipeline_meeting_docs__id'
          loading={historyLoading}
          pagination={versions.length > 10 ? { pageSize: 10, size: 'small' } : false}
          dataSource={versions}
          onRow={(row) => ({
            onClick: () => setViewId(row.pipeline_meeting_docs__id),
            className: row.pipeline_meeting_docs__id === viewId ? 'cursor-pointer bg-blue-50' : 'cursor-pointer'
          })}
          columns={[
            { title: '#', dataIndex: 'prog__revision', width: 50 },
            {
              title: 'Author',
              key: 'author',
              render: (_: unknown, row: MinutesVersion) => row.prog__author_name || 'Agent'
            },
            {
              title: 'When',
              dataIndex: 'generated_at',
              width: 140,
              render: (value: string | null) => (value ? dayjs(value).format('MM/DD/YYYY HH:mm') : '—')
            },
            {
              title: '',
              key: 'tags',
              render: (_: unknown, row: MinutesVersion) => {
                const kind = KIND_TAG[row.prog__kind]
                return (
                  <span className='flex flex-wrap gap-1'>
                    {row.is_current ? (
                      <Tag color='green' className='m-0'>
                        Current
                      </Tag>
                    ) : null}
                    <Tag color={kind.color} className='m-0'>
                      {kind.label}
                      {row.prog__kind === 'restore' && row.prog__restored_from_revision != null
                        ? ` from #${row.prog__restored_from_revision}`
                        : ''}
                    </Tag>
                  </span>
                )
              }
            },
            {
              title: '',
              key: 'actions',
              width: 140,
              render: (_: unknown, row: MinutesVersion) => (
                <span className='flex items-center gap-2' onClick={(e) => e.stopPropagation()}>
                  <button
                    type='button'
                    className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                    onClick={() => setViewId(row.pipeline_meeting_docs__id)}
                  >
                    View
                  </button>
                  {!row.is_current ? (
                    <Popconfirm
                      title={`Restore version ${row.prog__revision}?`}
                      description='Its content becomes the current minutes as a new version by you.'
                      okText='Restore'
                      onConfirm={() => restore(row)}
                    >
                      <button
                        type='button'
                        disabled={restoring != null}
                        className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left disabled:text-gray-400'
                      >
                        {restoring === row.pipeline_meeting_docs__id ? 'Restoring…' : 'Restore'}
                      </button>
                    </Popconfirm>
                  ) : null}
                </span>
              )
            }
          ]}
        />
        {viewed ? (
          <div className='mt-3'>
            <div className='flex items-center justify-between mb-1'>
              <span className='text-[11.5px] font-semibold uppercase tracking-wide text-gray-500'>
                Version {viewed.prog__revision}
                {previous ? ` — changes from version ${previous.prog__revision}` : ' — original'}
              </span>
              <span className='text-[11.5px] text-gray-500 flex items-center gap-1'>
                <Switch size='small' checked={showFull} onChange={setShowFull} /> Show full text
              </span>
            </div>
            {showFull || !previous ? (
              <div className='border border-gray-200 rounded p-2 bg-gray-50 overflow-y-auto' style={{ maxHeight: 420 }}>
                <MeetingDocBody doc={{ ...viewed, structured: {}, content_format: viewed.content_format ?? undefined }} />
              </div>
            ) : (
              <LineDiff before={previous.content} after={viewed.content} />
            )}
          </div>
        ) : null}
      </Modal>
    </FormSection>
  )
}
